import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { SubtleLink } from '@liveauctioneers/hammer-ui-core/link';
import { memo, useCallback, useEffect, useState } from 'react';

import { AddressVerifyModalData } from '@/components/Modals/AddressVerifyModal/types/AddressVerifyModal.types';
import { modalTypes } from '@/enums';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getStoredAddressSuggestion,
    getStoredOriginalAddress,
    getUpdateAccountHasLoaded,
} from '@/redux/modules/account/updateAccount/updateAccount';
import { updateBidderAddress } from '@/redux/modules/account/updateAccount/updateAccount.actions';
import { dismissModal, openAddressModal, openAddressVerifyModal } from '@/redux/modules/modal';
import { getOpenModal } from '@/redux/modules/modal.selectors';
import { BidderAddress } from '@/types/BidderAddresses';
import noop from '@/utils/noop';

import BidderAddressListItem from './BidderAddressListItem';

export type BidderAddressListProps = {
    addresses: BidderAddress[];
    canEdit: boolean;
    selectAddress?: (address: BidderAddress) => void;
    selectedAddress?: BidderAddress | undefined;
    setDefaultOnSelect: boolean;
    showRemoveButton?: boolean;
    showSetDefaultButton?: boolean;
    showTitle?: boolean;
};

const BidderAddressListComponent = ({
    addresses = [],
    canEdit,
    selectAddress = noop,
    selectedAddress,
    setDefaultOnSelect = false,
    showRemoveButton = false,
    showSetDefaultButton,
}: BidderAddressListProps) => {
    const dispatch = useAppDispatch();
    const [selectedAddressId, setSelectedAddressId] = useState(() => {
        if (selectedAddress?.addressId) {
            return selectedAddress.addressId;
        }
        if (addresses?.length) {
            const defaultAddress = addresses.find((address) => address.isDefault);
            return defaultAddress?.addressId ?? 0;
        }
        return 0;
    });
    const saveSucceeded = useAppSelector(getUpdateAccountHasLoaded);
    const suggestedAddress = useAppSelector(getStoredAddressSuggestion);

    const handleAddressSelected = useCallback(
        (address: BidderAddress) => {
            if (setDefaultOnSelect) {
                dispatch(updateBidderAddress(address.bidderId, { ...address, isDefault: true }, 'defaultUpdate'));
            } else {
                // for address selection outside the profile page
                selectAddress(address);
            }
            setSelectedAddressId(address.addressId);
        },
        [dispatch, selectAddress, setDefaultOnSelect]
    );

    const buildAddressList = useCallback(
        (address: BidderAddress) => {
            return (
                <BidderAddressListItem
                    address={address}
                    canEdit={canEdit}
                    key={`address-${address.addressId}`}
                    onSelect={handleAddressSelected}
                    selected={selectedAddressId === address.addressId}
                    selectedId={selectedAddressId || 0}
                    showRemoveButton={showRemoveButton}
                    showSetDefaultButton={showSetDefaultButton}
                />
            );
        },
        [canEdit, handleAddressSelected, selectedAddressId, showRemoveButton, showSetDefaultButton]
    );

    const openAddAddressModal = (id: number) => {
        dispatch(openAddressModal({ addressID: id }));
    };

    const openModal = useAppSelector(getOpenModal);
    const originallyEnteredAddress = useAppSelector(getStoredOriginalAddress);
    const [dismissedModal, setDismissedModal] = useState(false);

    useEffect(() => {
        if (saveSucceeded && openModal === modalTypes.ADDRESS) {
            dispatch(dismissModal());
            setDismissedModal(true);
        }
    }, [dispatch, openModal, saveSucceeded, suggestedAddress]);

    useEffect(() => {
        if (dismissedModal && Boolean(suggestedAddress)) {
            const data: AddressVerifyModalData = {
                suggestedAddress,
                userEnteredAddress: originallyEnteredAddress,
            };
            dispatch(openAddressVerifyModal(data));
        }
    }, [dismissedModal, dispatch, originallyEnteredAddress, suggestedAddress]);

    let sortedAddresses: BidderAddress[] = addresses;
    const defaultAddress = (sortedAddresses || []).find((address) => address.isDefault);

    if (defaultAddress) {
        sortedAddresses = [defaultAddress, ...addresses.filter((address) => !address.isDefault)];
    }

    return (
        <div
            className="max-w-[600px]"
            id="bidder-address-list"
        >
            {Boolean(sortedAddresses.length) && (
                <div className="mb-lg xsMax:mb-md">{sortedAddresses.map(buildAddressList)}</div>
            )}
            {Boolean(canEdit) && (
                <div className="flex max-w-[600px] items-center">
                    <SubtleLink
                        className="underline"
                        onClick={() => openAddAddressModal(0)}
                    >
                        <FormattedMessage id="addNewAddress" />
                    </SubtleLink>
                </div>
            )}
        </div>
    );
};

export const BidderAddressList = memo<BidderAddressListProps>(BidderAddressListComponent);
